import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../../hooks/useEntitlements';
import {
  CheckCircle2,
  Rocket,
  DollarSign,
  Users,
  Shield,
  Zap,
  TrendingUp,
  Briefcase,
  AlertTriangle,
  Clock,
  Target,
  X
} from 'lucide-react';

interface StageData {
  id: number;
  title: string;
  subtitle: string;
  icon: typeof Rocket;
  productId: string;
  price: number;
  marketPrice: number;
  color: string;
  position: { x: number; y: number };

  // Tooltip content (brief)
  tooltipText: string;

  // Full modal content
  whatYouGet: string[];
  whyItMatters: string;
  risksOfSkipping: string[];
  timeSavings: string;
  alternativeApproach: string;
  roi: string;
  duration: string;
  documentCount: number;
}

const stages: StageData[] = [
  {
    id: 0,
    title: 'Mission Control',
    subtitle: 'Launch Pad',
    icon: Briefcase,
    productId: 'lead-magnet',
    price: 0,
    marketPrice: 0,
    color: 'bg-green-500',
    position: { x: 10, y: 85 },
    tooltipText: 'Start here: Get your free SA Business Legal Launch Map showing all 4 stages',
    whatYouGet: [
      'Visual roadmap showing 4 stages from idea to scale',
      'Legal requirements at each stage',
      'Common mistakes checklist',
      'Document requirements per stage'
    ],
    whyItMatters: 'Understanding the legal journey prevents costly mistakes and helps you plan ahead.',
    risksOfSkipping: [
      'Flying blind without a roadmap',
      'Missing critical legal steps',
      'Reactive (expensive) vs proactive (cheap) approach'
    ],
    timeSavings: '2-3 hours of research condensed into 10-minute read',
    alternativeApproach: 'Spend hours Googling fragmented advice or pay R2,000+ for a lawyer consultation',
    roi: 'Free + saves you 3+ hours',
    duration: '10 minutes to review',
    documentCount: 1
  },
  {
    id: 1,
    title: 'Stage 1',
    subtitle: 'Liftoff',
    icon: Rocket,
    productId: 'quick-start-kit',
    price: 97,
    marketPrice: 350,
    color: 'bg-blue-500',
    position: { x: 20, y: 65 },
    tooltipText: 'Register your business, set up tax, banking, and basic legal structure',
    whatYouGet: [
      'Business structure decision guide (PTY vs Sole Proprietor)',
      'CIPC registration checklist',
      'Basic founders agreement template',
      'SARS tax registration guide',
      'Business banking setup checklist',
      'Implementation roadmap'
    ],
    whyItMatters: 'Operating without proper registration means you\'re personally liable for everything and face monthly CIPC fines.',
    risksOfSkipping: [
      'R1,000+ per month CIPC late filing penalties',
      'Personal liability for business debts',
      'Founder disputes with no legal protection',
      'Cannot open business bank account',
      'Tax non-compliance penalties'
    ],
    timeSavings: 'Saves 20+ hours of research and R5,000+ in lawyer consultations',
    alternativeApproach: 'Hire lawyer for R8,000-R15,000 for basic setup, or DIY and risk costly mistakes',
    roi: 'Avoid R5,000+ in fines + R15,000 in legal fees = R20,000+ value',
    duration: '1-2 weeks to implement',
    documentCount: 7
  },
  {
    id: 2,
    title: 'Stage 2',
    subtitle: 'Orbit',
    icon: DollarSign,
    productId: 'revenue-pack',
    price: 1997,
    marketPrice: 4500,
    color: 'bg-purple-500',
    position: { x: 40, y: 45 },
    tooltipText: 'Protect your first client relationships with proper contracts and agreements',
    whatYouGet: [
      'Service agreement template (B2B)',
      'Terms of Service (SaaS/online)',
      'Client onboarding pack',
      'NDA templates (mutual & unilateral)',
      'Independent contractor agreement',
      'Invoice template with payment terms',
      'Proposal & quote template',
      'Refund & cancellation policy',
      'POPIA client data guide'
    ],
    whyItMatters: 'Without proper contracts, clients can refuse payment, claim ownership of your work, or sue you for delivery issues.',
    risksOfSkipping: [
      'Scope creep (clients demanding free work)',
      'Payment disputes and late payments',
      'IP ownership conflicts',
      'POPIA violations (R10M fine)',
      'Contractor misclassification (SARS penalties)',
      'Refund/cancellation nightmares'
    ],
    timeSavings: 'Saves 40+ hours drafting contracts + R25,000+ in legal fees per contract',
    alternativeApproach: 'Pay R3,500-R8,000 per contract from lawyer, or use risky online templates',
    roi: '1 avoided payment dispute = 10x ROI. Prevents R50,000+ in legal costs.',
    duration: '2-4 weeks to implement',
    documentCount: 9
  },
  {
    id: 3,
    title: 'Stage 3',
    subtitle: 'Acceleration',
    icon: Users,
    productId: 'team-ops-pack',
    price: 3997,
    marketPrice: 7500,
    color: 'bg-orange-500',
    position: { x: 60, y: 30 },
    tooltipText: 'Hire employees, manage contractors, and scale operations legally',
    whatYouGet: [
      '4 employment contract types (permanent, fixed-term, part-time, remote)',
      'Contractor vs employee classification guide',
      'Non-compete & non-solicitation agreements',
      'Employee confidentiality agreement',
      'IP assignment agreement',
      'Employee handbook template',
      'Supplier/vendor agreement',
      'Master Service Agreement (MSA)',
      'Service Level Agreement (SLA)',
      'POPIA data processing agreement',
      'Office lease negotiation checklist'
    ],
    whyItMatters: 'Misclassifying contractors as employees can trigger R100,000+ SARS penalties plus back-taxes.',
    risksOfSkipping: [
      'SARS contractor reclassification fines',
      'Unfair dismissal claims (R50,000+ settlements)',
      'IP ownership disputes with employees',
      'Key employees leaving and taking clients',
      'POPIA violations from employee data mishandling',
      'Poor supplier agreements causing delays'
    ],
    timeSavings: 'Saves 60+ hours + R50,000+ in HR legal fees',
    alternativeApproach: 'Pay R5,000-R12,000 per employment contract + R15,000 for employee handbook',
    roi: '1 avoided unfair dismissal claim = 12x ROI. Prevents R200,000+ in disputes.',
    duration: '1-2 months to implement fully',
    documentCount: 11
  },
  {
    id: 4,
    title: 'Stage 4',
    subtitle: 'Moon Landing',
    icon: Shield,
    productId: 'protection-growth-pack',
    price: 6997,
    marketPrice: 12000,
    color: 'bg-red-500',
    position: { x: 80, y: 15 },
    tooltipText: 'Exit planning, M&A prep, franchising, and strategic growth protection',
    whatYouGet: [
      'Shareholders agreement (multi-shareholder)',
      'Board resolution templates',
      'Director service agreement',
      'Buy-sell agreement with valuation',
      'Partnership agreement',
      'Franchise agreement',
      'Dispute resolution procedures',
      'Business sale/acquisition LOI',
      'Due diligence checklist',
      'Succession planning guide',
      'Risk management audit'
    ],
    whyItMatters: 'Shareholder disputes without agreements can destroy R10M+ businesses in weeks.',
    risksOfSkipping: [
      'Shareholder deadlock (business freezes)',
      'Forced exits at unfair valuations',
      'Partnership disputes killing deals',
      'Failed M&A due to poor documentation',
      'Franchise brand damage from poor agreements',
      'Business collapse during succession'
    ],
    timeSavings: 'Saves 80+ hours + R100,000+ in corporate legal fees',
    alternativeApproach: 'Pay R25,000-R75,000 for shareholders agreement alone from top-tier firm',
    roi: 'Protecting a R5M business exit = 700x ROI. One avoided dispute = priceless.',
    duration: '2-3 months to implement strategically',
    documentCount: 11
  }
];

export default function JourneyNew() {
  const { entitlements } = useEntitlements();
  const navigate = useNavigate();
  const [hoveredStage, setHoveredStage] = useState<number | null>(null);
  const [expandedStage, setExpandedStage] = useState<number | null>(null);
  const [rocketPosition, setRocketPosition] = useState<{ x: number; y: number } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const hasStage = (productId: string) => {
    return entitlements.some(e => e.productId === productId && e.active);
  };

  const getStageStatus = (stage: StageData) => {
    if (hasStage(stage.productId)) return 'completed';
    return 'available'; // All stages are always available (no locking)
  };

  const getMissionStatusLabel = (stage: StageData) => {
    const status = getStageStatus(stage);
    if (status === 'completed') {
      if (stage.id === 0) return 'Free Legal Launch Map Received';
      return `Stage ${stage.id} Complete`;
    }
    return 'Mission Available';
  };

  // Find the last (highest) purchased stage
  const getLastPurchasedStage = () => {
    // Start from the end and find the highest purchased stage
    for (let i = stages.length - 1; i >= 0; i--) {
      if (hasStage(stages[i].productId)) {
        return stages[i];
      }
    }
    // If no stages purchased at all, return null (no rocket shown)
    return null;
  };

  // Initialize rocket position on mount and when entitlements change
  useEffect(() => {
    const lastStage = getLastPurchasedStage();
    setRocketPosition(lastStage ? lastStage.position : null);
  }, [entitlements]);

  // Animate rocket along the path
  const animateRocketTo = (targetStage: StageData) => {
    if (isAnimating || !rocketPosition) return;

    const currentStageIndex = stages.findIndex(
      s => s.position.x === rocketPosition.x && s.position.y === rocketPosition.y
    );
    const targetStageIndex = targetStage.id;

    if (currentStageIndex === targetStageIndex) return; // Already there

    setIsAnimating(true);

    // Build path of stages to visit (including skipped ones for visual effect)
    const pathStages: StageData[] = [];
    if (currentStageIndex < targetStageIndex) {
      // Moving forward
      for (let i = currentStageIndex + 1; i <= targetStageIndex; i++) {
        pathStages.push(stages[i]);
      }
    } else {
      // Moving backward
      for (let i = currentStageIndex - 1; i >= targetStageIndex; i--) {
        pathStages.push(stages[i]);
      }
    }

    // Animate through each stage in the path
    let currentIndex = 0;
    const animationInterval = setInterval(() => {
      if (currentIndex < pathStages.length) {
        setRocketPosition(pathStages[currentIndex].position);
        currentIndex++;
      } else {
        clearInterval(animationInterval);
        setIsAnimating(false);
      }
    }, 300); // 300ms per stage
  };

  const currentStage = stages.find(s => {
    const status = getStageStatus(s);
    return status === 'available' && !hasStage(s.productId);
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Your Business Launch Sequence
        </h1>
        <p className="text-slate-300 text-lg">
          From Launch Pad to Moon Landing: Navigate your legal flight plan 🚀
        </p>
        {currentStage && (
          <div className="mt-4 bg-blue-500/20 border border-blue-400/30 rounded-lg p-4">
            <p className="text-sm text-blue-200">
              🎯 <strong>Next Mission:</strong> {currentStage.title}: {currentStage.subtitle}
            </p>
          </div>
        )}
      </div>

      {/* Launch Map */}
      <div className="max-w-6xl mx-auto relative">
        {/* SVG Path (the rocket trajectory) */}
        <svg
          className="absolute inset-0 w-full h-[600px] pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.6" />
            </linearGradient>
          </defs>
          {/* The winding path */}
          <path
            d={`M ${stages[0].position.x} ${stages[0].position.y}
                L ${stages[1].position.x} ${stages[1].position.y}
                L ${stages[2].position.x} ${stages[2].position.y}
                L ${stages[3].position.x} ${stages[3].position.y}
                L ${stages[4].position.x} ${stages[4].position.y}`}
            stroke="url(#pathGradient)"
            strokeWidth="0.5"
            strokeDasharray="2,1"
            fill="none"
          />
        </svg>

        {/* Rocket (animated) */}
        {rocketPosition && (
          <div
            className="absolute z-30 transition-all duration-300 pointer-events-none"
            style={{
              left: `${rocketPosition.x}%`,
              top: `${rocketPosition.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="relative">
              <Rocket className={`w-8 h-8 text-yellow-400 ${isAnimating ? 'animate-bounce' : ''}`} />
              <div className="absolute inset-0 bg-yellow-400 opacity-50 blur-md rounded-full animate-pulse" />
            </div>
          </div>
        )}

        {/* Stage Markers */}
        <div className="relative h-[600px]">
          {stages.map((stage) => {
            const status = getStageStatus(stage);
            const Icon = stage.icon;
            const isHovered = hoveredStage === stage.id;
            const isExpanded = expandedStage === stage.id;

            return (
              <div
                key={stage.id}
                className="absolute"
                style={{
                  left: `${stage.position.x}%`,
                  top: `${stage.position.y}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {/* Stage Marker */}
                <button
                  className={`
                    relative group transition-all duration-300
                    ${status === 'completed' ? 'scale-100' : 'scale-110'}
                    ${isHovered ? 'scale-125 z-50' : 'z-10'}
                  `}
                  onMouseEnter={() => setHoveredStage(stage.id)}
                  onMouseLeave={() => setHoveredStage(null)}
                  onClick={() => {
                    setExpandedStage(stage.id);
                    if (status === 'completed') {
                      animateRocketTo(stage);
                    }
                  }}
                >
                  {/* Glow effect for next available stage */}
                  {status === 'available' && !hasStage(stage.productId) && (
                    <div className={`absolute inset-0 ${stage.color} opacity-50 blur-xl rounded-full animate-ping`} />
                  )}

                  {/* Main circle */}
                  <div className={`
                    relative w-20 h-20 rounded-full border-4 flex items-center justify-center
                    ${status === 'completed' ? 'bg-green-500 border-green-300' : `${stage.color} border-white`}
                    shadow-xl transition-all duration-300
                    hover:shadow-2xl
                  `}>
                    {status === 'completed' ? (
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    ) : (
                      <Icon className="w-10 h-10 text-white" />
                    )}
                  </div>

                  {/* Stage label */}
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
                    <p className="text-sm font-bold">{stage.title}: {stage.subtitle}</p>
                    <p className={`text-xs mt-1 ${status === 'completed' ? 'text-green-400' : 'text-blue-300'}`}>
                      {getMissionStatusLabel(stage)}
                    </p>
                    {stage.price > 0 && status !== 'completed' && (
                      <p className="text-xs mt-1">
                        <span className="text-green-400 font-bold">R{stage.price}</span>
                        {stage.marketPrice > 0 && (
                          <span className="text-slate-400 line-through ml-1 text-xs">
                            R{stage.marketPrice}
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Tooltip on hover */}
                  {isHovered && !isExpanded && (
                    <div className="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-64 bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-2xl z-50 pointer-events-none">
                      <p className="text-sm text-slate-200">{stage.tooltipText}</p>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                        <div className="border-8 border-transparent border-t-slate-800" />
                      </div>
                      <p className="text-xs text-blue-300 mt-2">👆 Click to learn more</p>
                    </div>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Expanded Detail Modal */}
      {expandedStage !== null && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {(() => {
              const stage = stages[expandedStage];
              const status = getStageStatus(stage);
              const Icon = stage.icon;

              return (
                <>
                  {/* Header */}
                  <div className={`${stage.color} p-6 rounded-t-2xl relative`}>
                    <button
                      onClick={() => setExpandedStage(null)}
                      className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition"
                    >
                      <X className="w-6 h-6" />
                    </button>

                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold text-white">{stage.title}: {stage.subtitle}</h2>
                        <div className="flex gap-4 mt-2 text-white/90 text-sm">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {stage.documentCount} documents
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {stage.duration}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mt-4 flex items-baseline gap-2">
                      {stage.price > 0 ? (
                        <>
                          <span className="text-4xl font-bold text-white">R{stage.price}</span>
                          {stage.marketPrice > 0 && (
                            <>
                              <span className="text-xl text-white/60 line-through">R{stage.marketPrice}</span>
                              <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                SAVE R{stage.marketPrice - stage.price}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-4xl font-bold text-white">FREE</span>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-6">
                    {/* What You Get */}
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5 text-blue-400" />
                        What You Get
                      </h3>
                      <ul className="space-y-2">
                        {stage.whatYouGet.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Why It Matters */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-blue-300 mb-2 flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Why This Matters
                      </h3>
                      <p className="text-slate-300">{stage.whyItMatters}</p>
                    </div>

                    {/* Risks of Skipping */}
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h3 className="text-lg font-bold text-red-300 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Risks of Skipping
                      </h3>
                      <ul className="space-y-2">
                        {stage.risksOfSkipping.map((risk, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <span className="text-red-400 font-bold flex-shrink-0">•</span>
                            <span>{risk}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Time & ROI */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                        <h4 className="text-sm font-bold text-slate-400 mb-1">⏱️ Time Savings</h4>
                        <p className="text-slate-200">{stage.timeSavings}</p>
                      </div>
                      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                        <h4 className="text-sm font-bold text-slate-400 mb-1">💰 ROI</h4>
                        <p className="text-green-400 font-bold">{stage.roi}</p>
                      </div>
                    </div>

                    {/* Alternative Approach */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                      <h4 className="text-sm font-bold text-slate-400 mb-2">🔄 Alternative Approach</h4>
                      <p className="text-slate-300">{stage.alternativeApproach}</p>
                    </div>

                    {/* CTA */}
                    <div className="flex gap-3">
                      {status === 'completed' ? (
                        <button
                          onClick={() => {
                            animateRocketTo(stage);
                            navigate('/portal/downloads');
                          }}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-lg transition flex items-center justify-center gap-2"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                          View Your Documents
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(`/portal/products/${stage.productId}`)}
                          className={`flex-1 ${stage.color} hover:opacity-90 text-white font-bold py-4 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg`}
                        >
                          <TrendingUp className="w-5 h-5" />
                          {stage.price > 0 ? `Get Stage ${stage.id} - R${stage.price}` : 'Get Free Roadmap'}
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedStage(null)}
                        className="px-6 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Mission Control Panel */}
      <div className="max-w-6xl mx-auto mt-8 bg-slate-800/50 border border-slate-700 rounded-lg p-4">
        <h3 className="text-sm font-bold text-slate-400 mb-3">Mission Control Panel:</h3>
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-slate-300">Mission Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center animate-pulse">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-slate-300">Next Recommended Mission</span>
          </div>
          <div className="flex items-center gap-2">
            <Rocket className="w-8 h-8 text-yellow-400" />
            <span className="text-slate-300">Your Rocket (click any completed stage to navigate)</span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          💡 Flight Plan: You can skip ahead to any stage, but sequential completion gives you the smoothest trajectory.
        </p>
      </div>
    </div>
  );
}
